import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Btn, Badge, Advanced } from "./common";
import { Mic, Activity, Ear } from "lucide-react";
import { toast } from "sonner";

const SR_SUPPORTED = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

/* "Jarvis diyerek uyandır" — per-user preference (default OFF). Enabling runs a FREE recognizer that, on the
   word "Jarvis", starts the real voice session. Honest about its limits; makes no background/lock promise. */
export const WakeWordToggleCard = () => {
  const { tx } = useI18n();
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diag, setDiag] = useState({}); // {listening, needsGesture, permission, reason}

  useEffect(() => {
    api.get("/settings").then((r) => { setEnabled(!!r.data?.wake_word_enabled); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);
  useEffect(() => {
    const h = (e) => setDiag((d) => ({ ...d, ...(e.detail || {}) }));
    window.addEventListener("jarvis-wake-status", h);
    return () => window.removeEventListener("jarvis-wake-status", h);
  }, []);

  const toggle = async () => {
    if (saving) return;
    const next = !enabled;
    setSaving(true);
    try {
      await api.put("/settings", { wake_word_enabled: next });
      setEnabled(next);
      window.dispatchEvent(new CustomEvent("jarvis-wake-set", { detail: { enabled: next } }));
      toast.success(next ? tx("Uyandırma sözcüğü açıldı") : tx("Uyandırma sözcüğü kapatıldı"));
    } catch (e) { toast.error(tx("Kaydedilemedi")); }
    finally { setSaving(false); }
  };

  const statusText = !SR_SUPPORTED ? tx("Bu tarayıcı ses tanımayı desteklemiyor — uyandırma sözcüğü kullanılamaz.")
    : !enabled ? tx("Kapalı. Açıldığında bir kez sayfayla etkileşmeniz ve mikrofon izni vermeniz gerekir.")
    : diag.permission === "denied" ? tx("Mikrofon izni reddedildi — tarayıcı adres çubuğundan izin verin.")
    : diag.reason === "session" ? tx("Oturum sürerken duraklatıldı (konuşma bitince tekrar dinler).")
    : diag.needsGesture && !diag.listening ? tx("Etkinleştirmek için sayfada bir yere dokunun/tıklayın.")
    : diag.listening ? tx("Dinlemede — 'Jarvis' deyin, konuşma başlasın.") : tx("Hazırlanıyor…");
  const statusColor = !SR_SUPPORTED || diag.permission === "denied" ? "amber"
    : (enabled && diag.listening) ? "green" : "gray";

  return (
    <div data-testid="wakeword-toggle-card">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-white/90"><Ear size={15} /> {tx("Jarvis diyerek uyandır")}</div>
          <p className="text-[0.7rem] text-white/50 mt-0.5">{tx("Açıkken, sekme açıkken \"Jarvis\" dediğinizde ses oturumu kendiliğinden başlar (beklerken ücretli oturum açılmaz).")}</p>
        </div>
        <button role="switch" aria-checked={enabled} aria-label={tx("Jarvis diyerek uyandır")}
          data-testid="wakeword-toggle" disabled={!SR_SUPPORTED || !loaded || saving} onClick={toggle}
          className={`relative shrink-0 h-7 w-12 rounded-full border transition-colors disabled:opacity-50 ${enabled ? "bg-cyan-500/30 border-cyan-400/60" : "bg-white/10 border-white/15"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-6" : "left-0.5"}`} />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2" data-testid="wakeword-status">
        <Badge color={statusColor}>{enabled && diag.listening ? tx("dinlemede") : enabled ? tx("açık") : tx("kapalı")}</Badge>
        <span className="text-[0.7rem] text-white/60">{statusText}</span>
      </div>

      {/* one short truthful limit stays visible; the detail lives in the collapsed block */}
      <p className="mt-2 text-[0.68rem] text-white/45" data-testid="wakeword-limits">
        {tx("Yalnızca bu sekme açık ve etkinken dinler; arka planda veya kilitli ekranda dinleme yoktur.")}
      </p>
      <Advanced label={tx("Ses ayrıntıları")} testid="wakeword-advanced">
        <div>{tx("Sekme kapanır, uykuya geçer veya tarayıcı sekmeyi askıya alırsa dinleme durur; kısa kesintilerde otomatik yeniden kurulur.")}</div>
        <div>{tx("Kapatınca dinleyici ve mikrofon erişimi tamamen bırakılır; süren bir konuşma kesilmez. Orb'a tıklayarak elle başlat/durdur her zaman çalışır.")}</div>
        <div>{tx("Mikrofon donanımı bu ortamda test edilmemiştir.")}</div>
      </Advanced>
    </div>
  );
};

/* Microphone device selection + a quick live level test + honest wake-word note.
   The chosen device id is stored in localStorage ("jarvis-mic-id") and is read by both the one-way
   voice session and the real-time Live session when they call getUserMedia. */
export const MicSettingsCard = () => {
  const { tx } = useI18n();
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(localStorage.getItem("jarvis-mic-id") || "");
  const [testing, setTesting] = useState(false);
  const [level, setLevel] = useState(0);
  const raf = useRef(null);
  const stop = useRef(null);

  const refresh = async () => {
    try {
      // a one-shot permission grant is needed before device labels are exposed by the browser
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
    } catch (e) { toast.error(tx("Mikrofon izni verilmedi veya cihaz bulunamadı.")); }
  };
  useEffect(() => { refresh(); return () => endTest(); /* eslint-disable-next-line */ }, []);

  const choose = (id) => { setSelected(id); if (id) localStorage.setItem("jarvis-mic-id", id); else localStorage.removeItem("jarvis-mic-id"); toast.success(tx("Mikrofon kaydedildi")); };

  const endTest = () => {
    if (raf.current) cancelAnimationFrame(raf.current);
    if (stop.current) stop.current();
    stop.current = null; setTesting(false); setLevel(0);
  };
  const test = async () => {
    if (testing) return endTest();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: selected ? { deviceId: { exact: selected } } : true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      setTesting(true);
      const loop = () => {
        an.getByteTimeDomainData(buf);
        let peak = 0; for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i] - 128));
        setLevel(Math.min(100, Math.round((peak / 128) * 160)));
        raf.current = requestAnimationFrame(loop);
      };
      loop();
      stop.current = () => { stream.getTracks().forEach((t) => t.stop()); ctx.close(); };
      setTimeout(() => endTest(), 8000); // auto-stop so the mic never stays open
    } catch (e) { toast.error(tx("Mikrofon testi başlatılamadı.")); }
  };

  return (
    <div data-testid="mic-settings">
      <div className="label-eyebrow mb-1.5">{tx("Mikrofon cihazı")}</div>
      <div className="flex gap-2 items-center flex-wrap">
        <select value={selected} onChange={(e) => choose(e.target.value)} data-testid="mic-device-select"
          className="flex-1 min-w-[180px] bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white">
          <option value="" className="bg-[#0C1222]">{tx("Sistem varsayılanı")}</option>
          {devices.map((d, i) => <option key={d.deviceId} value={d.deviceId} className="bg-[#0C1222]">{d.label || tx("Mikrofon {n}", { n: i + 1 })}</option>)}
        </select>
        <Btn variant="ghost" onClick={refresh} data-testid="mic-refresh-btn">{tx("Yenile")}</Btn>
        <Btn onClick={test} data-testid="mic-test-btn"><Mic size={13} className="inline mr-1" />{testing ? tx("Durdur") : tx("Test et")}</Btn>
      </div>

      {testing && (
        <div className="mt-3" data-testid="mic-level">
          <div className="flex items-center gap-2 text-xs text-cyan-300 mb-1"><Activity size={13} /> {tx("Konuşun — seviye:")}</div>
          <div className="h-2.5 w-full bg-black/40 rounded-full overflow-hidden">
            <div className="h-full bg-cyan-400 transition-[width] duration-75" style={{ width: `${level}%` }} />
          </div>
          <p className="text-[0.66rem] text-white/40 mt-1">{tx("Çubuk hareket ediyorsa mikrofon çalışıyor. Test 8 sn sonra otomatik durur.")}</p>
        </div>
      )}

      <Advanced label={tx("Ses ayrıntıları")} testid="wakeword-note">
        <div>{tx("Araya girme (barge-in): Jarvis konuşurken konuşmaya başlarsanız yanıt kesilir.")}</div>
        <div>{tx("Mikrofon donanımı bu ortamda test edilmemiştir.")}</div>
      </Advanced>
    </div>
  );
};

export default MicSettingsCard;
